import { ListStore, memone } from '@deqo/foundation';
import { injectable } from 'inversify';
import { Arr } from '../../../CodesForFoundation';
import { CommandDispatcher } from '../../../concepts/commands/CommandDispatcher';
import { Connector, resolve } from '../../Container';
import { EditorSession } from '../EditorSession';
import { SuggestedWidgetScale } from '../workspace/SuggestedWidgetScaleProperty';
import { WidgetManager } from '../workspace/widgets/WidgetManager';
import { toolEquip } from './ToolCommands';
import { ToolDefinition } from './ToolDefinition';
import { ToolInfo } from './ToolInfo';
import { ToolManager } from './ToolManager';
import { ToolboxItem } from './ToolboxItem';

@injectable()
export class ToolboxController extends ListStore<ToolboxItem> {

    @resolve(CommandDispatcher) private dispatch!: CommandDispatcher;
    @resolve(EditorSession) private session!: EditorSession;
    @resolve(ToolManager) private tools!: ToolManager;
    @resolve(WidgetManager) private widgets!: WidgetManager;

    private get reverseIndex() {
        return this.buildReverseIndex(this.getState());
    }

    public start() {
        this.burden(
            this.session.observe(SuggestedWidgetScale, this.sync),
            this.session.observe(ToolInfo, this.realizeActiveTool),
            this.tools.observe(this.sync),
            this.observe(this.sync),
        );
        this.sync();
    }

    public register(...items: ToolboxItem[]) {
        this.updateState(s => void (s.push(...items)));
    }

    public openWidget(id: string) {
        const toolbox = this.widgets.get('toolbox');
        toolbox.find(w => w.name === id)?.update({ open: true });
    }

    public openWidgetForTool(toolId: string) {
        this.openWidget(this.reverseIndex.get(toolId));
    }

    private sync = () => {
        this.realizeToolboxWidget();
        this.realizeActiveTool();
    };

    private realizeToolboxWidget() {
        const { dispatch } = this;

        this.widgets.delete('toolbox');

        const dock = this.widgets.createContainer({
            name: 'toolbox',
            weight: 2000,
            position: ['docked', 'topLeft', 'stack'],
            direction: 'column',
            scale: this.computeContainerScale(),
        });

        const closeOthers = (w: any) => {
            for (const o of dock) {
                if (o.type === 'popout' && o !== w) {
                    o.update({ open: false });
                }
            }
        };

        const items = Arr.sortBy(this.state, 'weight');

        for (const tm of items) {

            // At least one tool must be enabled
            const firstTool = tm.tools.find(x =>
                this.tools.isEnabled(x.id) && this.tools.isVisible(x.id));
            if (!firstTool) continue;

            const ws: any = {
                name: tm.id,
                image: tm.icon ?? firstTool.icon,
                text: formatLabel(tm, firstTool),
                weight: tm.weight,
            };

            if (tm.ui) {
                Object.assign(ws, {
                    tag: 'pingable',
                    onOpen() {
                        closeOthers(this);
                        if (!tm.skipEquip) {
                            dispatch(toolEquip, [firstTool.id]);
                        }
                    },
                });

                dock.add(this.widgets.createPopout({ ui: tm.ui, placement: tm.placement, ...ws }));
            }
            else {
                dock.add(this.widgets.createButton({
                    ...ws,
                    tag: 'pingable',
                    onAction() {
                        closeOthers(this);
                        dispatch(toolEquip, [firstTool.id]);
                    },
                }));
            }
        }

        this.widgets.add(dock);
    }

    private realizeActiveTool = (next?: ToolInfo, prev?: ToolInfo) => {
        next = next ?? this.session.get(ToolInfo);
        // If tool has changed, update the buttons states appropriately
        if (next?.id === prev?.id) return;
        const toolbox = this.widgets.get('toolbox');
        if (!toolbox) return;
        toolbox.forEach(w => {
            w.update({ active: w.name === this.reverseIndex.get(next.id) });
        });
    };

    private computeContainerScale = () => {
        const sws = this.session.get(SuggestedWidgetScale);
        if (sws === 'smallest') return 1.25;
        if (sws === 'small') return 1.5;
        else return 2;
    };

    @memone()
    private buildReverseIndex(items: ToolboxItem[]) {
        const index = new Map<string, string>();
        for (const item of items) {
            for (const tool of item.tools) {
                index.set(tool.id, item.id);
            }
        }
        return index;
    }
}

export const inToolboxItems = (...items: ToolboxItem[]): Connector =>
    r => void (r.call(ToolboxController).register(...items));

const formatLabel = (item: ToolboxItem, firstTool: ToolDefinition) => {
    const label = item.label ?? firstTool.label;
    const shortcut = item.shortcut ?? firstTool.shortcut;
    return !!shortcut ? `${label} (${shortcut})` : label;
};
